import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Search, Visibility, FilterList, Close, Delete } from '@mui/icons-material';
import { customerInvoiceService } from '../services/customerInvoiceService';
import { formatCurrency } from '../components/CurrencyTextField';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { formatDateOnly, parseLocalDate } from '../utils/dateUtils';

const CustomerInvoices = () => {
  const DEFAULT_COL_WIDTHS = {
    invNo: 140,
    customer: 220,
    orderNo: 130,
    invDate: 120,
    dueDate: 120,
    total: 120,
    status: 120,
    actions: 110
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partFilter = searchParams.get('part_id');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sortKey, setSortKey] = useState('invoice_number');
  const [sortDirection, setSortDirection] = useState('desc');
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_customer_invoices_col_widths',
    minWidths: {
      invNo: 110, customer: 150, orderNo: 100, invDate: 100, dueDate: 100, total: 100, status: 100, actions: 90
    }
  });

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        search: tableSearch || undefined,
        status: statusFilter || undefined,
        ...(partFilter ? { part_id: partFilter } : {}),
        limit: 200
      };
      const response = await customerInvoiceService.getAll(params);
      const list = response.data || [];
      setInvoices(list);
      setSelectedInvoice(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Error loading customer invoices:', err);
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [statusFilter, partFilter]);

  const filteredInvoices = useMemo(() => {
    let list = invoices;
    const lower = (s) => String(s ?? '').toLowerCase();
    const searchLower = lower(tableSearch).trim();
    if (searchLower) {
      list = list.filter((inv) => {
        const invNum = lower(inv.identifier ?? inv.invoice_number ?? '');
        const cust = lower(inv.company_name || `${inv.customer_first_name || ''} ${inv.customer_last_name || ''}`);
        const orderNum = lower(String(inv.work_order_identifier ?? inv.work_order_number ?? ''));
        return invNum.includes(searchLower) || cust.includes(searchLower) || orderNum.includes(searchLower);
      });
    }
    return list;
  }, [invoices, tableSearch]);

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedInvoices = useMemo(() => {
    const list = [...filteredInvoices];
    const getter = (row) => {
      switch (sortKey) {
        case 'invoice_number': return row.identifier ?? row.invoice_number ?? '';
        case 'customer': return row.company_name || `${row.customer_first_name || ''} ${row.customer_last_name || ''}`.trim();
        case 'work_order_number': return row.work_order_identifier ?? row.work_order_number ?? '';
        case 'invoice_date': return row.invoice_date ? (parseLocalDate(row.invoice_date)?.getTime() ?? 0) : 0;
        case 'due_date': return row.due_date ? (parseLocalDate(row.due_date)?.getTime() ?? 0) : 0;
        case 'total_amount': return Number(row.total_amount) || 0;
        case 'status': return row.status ?? '';
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [filteredInvoices, sortKey, sortDirection]);

  const getStatusColor = (status) => {
    const colors = { Due: 'info', 'Past Due': 'error', Paid: 'success' };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    return formatDateOnly(dateString, 'MM/dd/yyyy') || '—';
  };

  const renderOrderLink = (invoice) => {
    const orderLabel = invoice?.work_order_identifier ?? invoice?.work_order_number ?? '—';
    if (!invoice?.work_order_id) return orderLabel;
    return (
      <Button
        size="small"
        variant="text"
        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/work-orders/${invoice.work_order_id}`);
        }}
      >
        {orderLabel}
      </Button>
    );
  };

  const renderCustomerLink = (invoice) => {
    const customerLabel = invoice?.company_name ||
      [invoice?.customer_first_name, invoice?.customer_last_name].filter(Boolean).join(' ') ||
      '—';
    if (!invoice?.customer_id) return customerLabel;
    return (
      <Button
        size="small"
        variant="text"
        sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/customers/${invoice.customer_id}`);
        }}
      >
        {customerLabel}
      </Button>
    );
  };

  const handleDeleteClick = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setInvoiceToDelete(null);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete?.id) return;
    try {
      setDeleting(true);
      setError('');
      await customerInvoiceService.delete(invoiceToDelete.id);
      handleDeleteDialogClose();
      await loadInvoices();
    } catch (err) {
      console.error('Error deleting customer invoice:', err);
      setError(err.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Customer Invoices
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Selected Invoice Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedInvoice?.identifier ?? selectedInvoice?.invoice_number ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">{renderCustomerLink(selectedInvoice)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order #</Typography>
            <Typography variant="body2">{renderOrderLink(selectedInvoice)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer PO</Typography>
            <Typography variant="body2">{selectedInvoice?.po_number || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice Date</Typography>
            <Typography variant="body2">{selectedInvoice ? formatDate(selectedInvoice.invoice_date) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Due Date</Typography>
            <Typography variant="body2">{selectedInvoice ? formatDate(selectedInvoice.due_date) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedInvoice ? formatCurrency(selectedInvoice.total_amount, '—') : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            <Chip
              label={selectedInvoice?.status ?? '—'}
              color={getStatusColor(selectedInvoice?.status)}
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={() => setSelectedInvoice(null)} disabled={!selectedInvoice} sx={{ ml: 'auto' }} title="Clear selection">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search invoice #, customer, order #"
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadInvoices()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Due">Due</MenuItem>
              <MenuItem value="Past Due">Past Due</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={() => selectedInvoice && navigate(`/customer-invoices/${selectedInvoice.id}`)} disabled={!selectedInvoice}>
            View
          </Button>
          <IconButton size="small" onClick={loadInvoices} title="Search">
            <FilterList />
          </IconButton>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <ResizableHeaderCell width={colWidths.invNo} onResizeStart={(e) => startResize('invNo', e)} sortable sortActive={sortKey === 'invoice_number'} sortDirection={sortDirection} onSort={() => handleSort('invoice_number')}>Invoice #</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.customer} onResizeStart={(e) => startResize('customer', e)} sortable sortActive={sortKey === 'customer'} sortDirection={sortDirection} onSort={() => handleSort('customer')}>Customer</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.orderNo} onResizeStart={(e) => startResize('orderNo', e)} sortable sortActive={sortKey === 'work_order_number'} sortDirection={sortDirection} onSort={() => handleSort('work_order_number')}>Order #</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.invDate} onResizeStart={(e) => startResize('invDate', e)} sortable sortActive={sortKey === 'invoice_date'} sortDirection={sortDirection} onSort={() => handleSort('invoice_date')}>Invoice Date</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.dueDate} onResizeStart={(e) => startResize('dueDate', e)} sortable sortActive={sortKey === 'due_date'} sortDirection={sortDirection} onSort={() => handleSort('due_date')}>Due Date</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.total} onResizeStart={(e) => startResize('total', e)} align="right" sortable sortActive={sortKey === 'total_amount'} sortDirection={sortDirection} onSort={() => handleSort('total_amount')}>Total</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.status} onResizeStart={(e) => startResize('status', e)} sortable sortActive={sortKey === 'status'} sortDirection={sortDirection} onSort={() => handleSort('status')}>Status</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)} align="right" sx={{ fontWeight: 600 }}>Actions</ResizableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                displayedInvoices.map((inv) => (
                  <TableRow
                    key={inv.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedInvoice?.id === inv.id ? 'action.selected' : 'inherit'
                    }}
                    onClick={() => setSelectedInvoice(inv)}
                    onDoubleClick={() => navigate(`/customer-invoices/${inv.id}`)}
                  >
                    <TableCell sx={{ width: colWidths.invNo, fontWeight: 500 }}>{inv.identifier ?? inv.invoice_number}</TableCell>
                    <TableCell sx={{ width: colWidths.customer }}>
                      {renderCustomerLink(inv)}
                    </TableCell>
                    <TableCell sx={{ width: colWidths.orderNo }}>{renderOrderLink(inv)}</TableCell>
                    <TableCell sx={{ width: colWidths.invDate }}>{formatDate(inv.invoice_date)}</TableCell>
                    <TableCell sx={{ width: colWidths.dueDate }}>{formatDate(inv.due_date)}</TableCell>
                    <TableCell sx={{ width: colWidths.total }} align="right">{formatCurrency(inv.total_amount, '—')}</TableCell>
                    <TableCell sx={{ width: colWidths.status }}>
                      <Chip
                        label={inv.status}
                        color={getStatusColor(inv.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: colWidths.actions }} align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/customer-invoices/${inv.id}`)}
                        title="View"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(inv)}
                        title="Delete"
                        disabled={deleting}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-invoice-dialog-title"
      >
        <DialogTitle id="delete-invoice-dialog-title">Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete invoice {invoiceToDelete?.identifier ?? invoiceToDelete?.invoice_number ?? ''}? Packing slips are not deleted. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleting}>Cancel</Button>
          <Button onClick={confirmDeleteInvoice} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerInvoices;
